/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace whitespace */

import {
  from,
  Observable
}                                from 'rxjs';
import { mergeMap }              from 'rxjs/operators';

import { Injectable  }           from '@angular/core';
import { HttpClient }           from '@angular/common/http';
import { HttpHeaders }           from '@angular/common/http';
import { HttpParams  }           from '@angular/common/http';
import { AuthenticationService } from './authentication.service';

import {
  Blob as BlobFile,
  BlobType
}                                from '@/models/blob';
import { User }                  from '@/models/user';
import { MD5 }                   from '@/util/md5';
import { Logger }                from '@/util/logger';



@Injectable({
  providedIn: 'root'
})

/**
 *
 * @class BlobService
 *
 * @brief CRUD access to blobs (virtualized and version control ready files)
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2020
 *
 */
export class BlobService {

  constructor(
    private http: HttpClient,
    private auth: AuthenticationService
  ) {

    Logger.debug(`[BlobService] constructing...`);
  }

  /** convert a string into a binary form but 8-bit ascii encoded.  Allow for unicode etc. */
  binaryKey(key: string): string {

    /* ref: https://stackoverflow.com/questions/30106476/using-javascripts-atob-to-decode-base64-doesnt-properly-decode-utf-8-strings */

    const codeUnits = new Uint16Array(key.length);

    let byteArray: number[] = [];

    for (let i = 0; i < codeUnits.length; i++) {

      let byte: number = key.charCodeAt(i);

      /* low byte first */

      byteArray.push(byte & 0xFF);

      /* high byte second */

      byteArray.push((byte >> 8) & 0xFF);
    }

    return btoa(String.fromCharCode(...byteArray));
  }

  /** fetch any headers we need to send along with our request */
  headers() : HttpHeaders {

    const user: User = this.auth.userOfSession;

    if (!user) {

      /* no user logged in */

      Logger.error(`[BlobService] headers - no user logged in!`);

      return null;
    }

    return new HttpHeaders().set('Authorization', `Bearer ${user.token}`);
  }

  /** test to see if this blob is a scenario */
  isScenario(path: string, instance: string) {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] isScenario - no user logged in!`);

      return null;
    }

    const params: HttpParams  = new HttpParams().
    set('key',      path).
    set('bkey',     this.binaryKey(path)).
    set('instance', instance);

    return this.http.get<string[]>(`/backend/blob/is/scenario`, {
      headers,
      params
    });
  }

  /** test to see if this blob is a simulation output */
  isOutput(path: string, instance: string) {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] isOutput - no user logged in!`);

      return null;
    }

    const params: HttpParams  = new HttpParams().
    set('key',      path).
    set('bkey',     this.binaryKey(path)).
    set('instance', instance);

    return this.http.get<string[]>(`/backend/blob/is/output`, {
      headers,
      params
    });
  }

  /** fetch postgresql disk usage */
  dbSize(): Observable<string> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] dbSize - no user logged in!`);

      return null;
    }

    return this.http.get<string>(`/backend/report/dbsize`, {
      headers
    });
  }

  /** fetch postgresql disk usage */
  diskCapacity(): Observable<string[]> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] diskCapacity - no user logged in!`);

      return null;
    }

    return this.http.get<string[]>(`/backend/report/diskcapacity`, {
      headers
    });
  }

  /** fetch postgresql disk usage */
  diskHogs(username: string=''): Observable<string[]> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] diskHogs - no user logged in!`);

      return null;
    }

    username = username.trim();

    if (username.length == 0) {

      return this.http.get<string[]>(`/backend/report/diskhogs`, {
        headers
      });

    } else {

      return this.http.get<string[]>(`/backend/report/diskhogs/${username}`, {
        headers
      });
    }

  }

  /** fetch the possible kinds of blobs */
  types(): Observable<string[]> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] types - no user logged in!`);

      return null;
    }

    return this.http.get<string[]>(`/backend/blob/types`, {
      headers
    });
  }

  /** check to see if there is either a blob or a folder implied by blobs at this key */
  fileordir(path: string, instance: string='latest'): Observable<Object> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] fileordir - no user logged in!`);

      return null;
    }

    const params: HttpParams  = new HttpParams().
    set('key',      path).
    set('bkey',     this.binaryKey(path)).
    set('instance', instance);

    /* pass back meta for a blob */

    return this.http.get(`/backend/blob/fileordir`, {
      headers,
      params
    });
  }

  /** fetch the metadata for a blob */
  info(path: string, instance: string='latest'): Observable<Object> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] info - no user logged in!`);

      return null;
    }

    const params: HttpParams  = new HttpParams().
      set('key',      path).
      set('bkey',     this.binaryKey(path)).
      set('instance', instance);

    /* pass back meta for a blob */

    return this.http.get(`/backend/blob/info`, {
      headers,
      params
    });
  }

  /** fetch the full history of a blob */
  history(path: string): Observable<Object> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] history - no user logged in!`);

      return null;
    }

    const params: HttpParams = new HttpParams().
           set('key',      path).
           set('bkey',     this.binaryKey(path));

    /* pass back meta for each snapshot in the blob's history */

    return this.http.get(`/backend/blob/history`, {
      headers,
      params
    });

  }

  /** copy a blob, to a new place */
  copy(
    path:        string,
    dest:        string,
    instance:    string = 'latest',
    name:        string = '',
    type:        string = ''
  ): Observable<Object> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] copy - no user logged in!`);

      return null;
    }

    const params: HttpParams = new HttpParams().
    set('key',      path).
    set('bkey',     this.binaryKey(path)).
    set('dest',     dest).
    set('name',     name).
    set('type',     type).
    set('instance', instance);

    /* pass back meta for a blob */
    return this.http.put(`/backend/blob/copy`, '', {
      headers,
      params
    });
  }

  /** create a new blob, by importing the contents from a Google Buckets blob */
  googleImport(
    src: string,
    path:        string,
    type:        BlobType,
    name:        string  = '',
    description: string  = '',
    engine:      string  = '',
    ): Observable<any> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] googleImport - no user logged in!`);

      return null;
    }

    Logger.debug(`[BlobService] googleImport - importing google bucket blob: ${src} => ${path} ...`);

    const form: FormData = new FormData();

    form.set('key',         path);
    form.set('bkey',        this.binaryKey(path));
    form.set('type',        BlobFile.stringOfType(type));
    form.set('name',        name);
    form.set('description', description);
    form.set('src',         src);
    form.set('engine',      engine);

    return this.http.post(`/backend/blob/import/google`, form, {
      headers
    });

  }

  /** create a new blob from a Browser (user) uploaded file */
  create(
    path:        string,
    type:        BlobType,
    content:     File|string,
    name:        string='',
    description: string='',
    verify:      boolean = false,
    progress:    boolean = false
  ): Observable<any> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] create - no user logged in!`);

      return null;
    }

    Logger.debug(`[BlobService] (create) hashing content...`);

    return from(this.md5sum(content, verify)).pipe(mergeMap(
      (md5sum: string): Observable<any> => {

        Logger.debug(`[BlobService] (create) preparing request (${md5sum})...`);

        const form: FormData = new FormData();

        form.set('key',  path);
        form.set('bkey', this.binaryKey(path));
        form.set('type', BlobFile.stringOfType(type));

        if (typeof content === 'string') {

          /*
           * up-convert simple strings to upload files.  we try to never send stuff as a
           * POST body unless we truly have to, so we can avoid buffering data in memory
           * when things queue up in the backend. We want Apache (the /backend web server)
           * to be able to just drop everything on to the disk as temporary files.
           *
           */

          const blob: Blob = new Blob([content], { type: 'text/plain' });
          const file: File = new File([blob], "snapshot-blob.txt", {type: "text/plain"});

          /* use the given File object as a file upload */

          form.set('upload', file);

        } else {

          form.set('upload', content, name);
        }

        form.set('name',        name);
        form.set('description', description);
        form.set('md5sum',      md5sum);

        /* pass back meta for the new blob */

        if (progress) {

          /* generate HttpEvent objects */

          Logger.debug(`[BlobService] (create) posting request with progress events...`);

          return this.http.post(`/backend/blob/create`, form, {
            headers,
            reportProgress: true,
            observe: 'events'
          });

        }

        /* just generate the final completed even which includes the response body */

        Logger.debug(`[BlobService] (create) posting request...`);

        return this.http.post(`/backend/blob/create`, form, {
          headers
        });

      }));

  }

  /** fetch the contents of a blob, format can be 'arraybuffer' | 'blob' | 'json' | 'text' */
  contents(path: string, instance: string='latest', format: string='text'): Observable<Object> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] contents - no user logged in!`);

      return null;
    }

    const params: HttpParams  = new HttpParams().
      set('key',      path).
      set('bkey',     this.binaryKey(path)).
      set('instance', instance);

    /*
     * pass back content of a blob, not parsed, just the content.  We may later add
     * a binary method for contents() which casts the response as an array buffer or
     * a blob.  For now just using text, since not all blobs will be JSON format.
     *
     */

    switch (format) {

      case 'arraybuffer':
        return this.http.get(`/backend/blob/content`, {
            headers,
            params,
            responseType: 'arraybuffer'
          });

      case 'blob':
        return this.http.get(`/backend/blob/content`, {
          headers,
          params,
          responseType: 'blob'
        });

      case 'json':
        return this.http.get(`/backend/blob/content`, {
          headers,
          params,
          responseType: 'json'
        });

      case 'text':
        return this.http.get(`/backend/blob/content`, {
          headers,
          params,
          responseType: 'text'
        });

      default: return null;

    }

  }

  /** overwrite contents of the given blob (does not create a new snapshot) */
  update(path: string, content: File|string, instance: string='latest', verify: boolean = false): Observable<Object> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] update - no user logged in!`);

      return null;
    }

    Logger.debug(`[BlobService] (update) hashing content...`);

    return from(this.md5sum(content, verify)).pipe(mergeMap(
      (md5sum: string) => {

        Logger.debug(`[BlobService] (update) preparing request (${md5sum})...`);

        const form: FormData = new FormData();

        form.set('key',      path);
        form.set('instance', instance);
        form.set('md5sum',   md5sum);

        if (typeof content === 'string') {

          /*
           * up-convert simple strings to upload files.  we try to never send stuff as a
           * POST body unless we truly have to, so we can avoid buffering data in memory
           * when things queue up in the backend. We want Apache (the /backend web server)
           * to be able to just drop everything on to the disk as temporary files.
           *
           */

          const blob: Blob = new Blob([content], { type: 'text/plain' });
          const file: File = new File([blob], "snapshot-blob.txt", {type: "text/plain"});

          /* use the given File object as a file upload */

          form.set('upload', file);

        } else {

          form.set('upload', content, File.name);
        }

        /* pass back meta for the updated blob */

        Logger.debug(`[BlobService] (update) posting request...`);

        return this.http.post(`/backend/blob/update`, form, {
          headers
        });

      }));

  }

  /** overwrite contents of the given blob (does not create a new snapshot) */
  snapshot(path: string, content: File|string, name: string, type: BlobType, verify: boolean = false): Observable<any> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] snapshot - no user logged in!`);

      return null;
    }

    Logger.debug(`[BlobService] (snapshot) hashing content...`);

    return from(this.md5sum(content, verify)).pipe(mergeMap(
      (md5sum: string) => {

        Logger.debug(`[BlobService] (snapshot) preparing request (${md5sum})...`);

        const form = new FormData();

        form.set('key',    path);
        form.set('type',   BlobFile.stringOfType(type));
        form.set('name',   name);
        form.set('md5sum', md5sum);

        if (typeof content === 'string') {

          /*
           * up-convert simple strings to upload files.  we try to never send stuff as a
           * POST body unless we truly have to, so we can avoid buffering data in memory
           * when things queue up in the backend. We want Apache (the /backend web server)
           * to be able to just drop everything on to the disk as temporary files.
           *
           */

          const blob: Blob = new Blob([content], { type: 'text/plain' });
          const file: File = new File([blob], "snapshot-blob.txt", {type: "text/plain"});

          /* use the given File object as a file upload */

          form.set('upload', file);

        } else {

          /* use the given File object as a file upload */

          form.set('upload', content);
        }

        /* pass back meta for the new blob */

        Logger.debug(`[BlobService] (snapshot) posting request...`);

        return this.http.post(`/backend/blob/snapshot`, form, {
          headers
        });

      }));

  }

  /** helper to find the MD5SUM for the string (or file) via a promise */
  md5sum(content: File|string, verify: boolean = false): Promise<string> {

    if (verify === false) {

      Logger.debug(`[BlobService] (md5sum) !verify`);

      /* we aren't doing file hashing */

      return new Promise((resolve, reject) => {

        resolve('');
      });
    }

    if (typeof content === 'string') {

      /* we can do it right away! */

      return new Promise((resolve, reject) => {

        Logger.debug(`[BlobService] (md5sum) hashing string sz=${content.length}`);

        const md5sum: string = MD5.hex(content);

        Logger.debug(`[BlobService] (md5sum) hash=${md5sum}`);

        resolve(md5sum);
      });

    } else {

      /* have to async read in the file, then fill the promise ... */

      const reader: FileReader  = new FileReader();

      return new Promise((resolve, reject): void => {

        Logger.debug(`[BlobService] (md5sum) hashing file name=${content.name}`);

        reader.onload = (): void =>  {

          const  text: string = reader.result as string;

          const md5sum: string = MD5.hex(text);

          Logger.debug(`[BlobService] (md5sum) hash=${md5sum}`);

          resolve(md5sum);

        };

        reader.onerror = (e: ProgressEvent<FileReader>): void => {

          reject(e.target.error);
        };

        reader.readAsText(content);
      });
    }

  }

  /** delete everything (including all snapshots) that begins with this prefix (watch out !!) */
  removePrefix(key: string): Observable<Object>  {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] removePrefix - no user logged in!`);

      return null;
    }

    const params: HttpParams  = new HttpParams()
      .set('key',      key)
      .set('bkey',     this.binaryKey(key));

    return this.http.delete(`/backend/blob/delete/prefix`, {
      headers,
      params
    });
  }

  /** delete a snapshot, if you make complete=true, entire history will be wiped out */
  remove(key: string, instance: string='latest', complete: boolean=false): Observable<Object>  {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] remove - no user logged in!`);

      return null;
    }

    const params: HttpParams  = new HttpParams()
      .set('key',      key)
      .set('bkey',     this.binaryKey(key))
      .set('instance', instance)
      .set('complete', complete.toString());

    return this.http.delete(`/backend/blob/delete`, {
      headers,
      params
    });
  }

  /** quick matching */
  quickMatch(
    term:   string  = '',
    mode:   string  = '',
    cs:     boolean = true,
    offset: number  = 0,
    limit:  number  = 20
  ): Observable<Object> {

    const headers = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] quickMatch - no user logged in!`);

      return null;
    }

    const params: HttpParams  = new HttpParams()
      .set('term',    term)
      .set('mode',    mode)
      .set('case',    cs ? 'sensitive' : '')
      .set('offset',  offset.toString())
      .set('limit',   limit.toString());

    /* pass back this hits */

    return this.http.get(`/backend/blob/quicksearch`, {
      headers,
      params
    });

  }

  /** the 'ls' command, but for blobs :) */
  list(
    hidden:     boolean,
    path:       string,
    delimiter?: string,
    offset?:    number,
    limit?:     number,
    key?:       string,
    guid?:      string,
    type?:      BlobType
  ): Observable<Object> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] list - no user logged in!`);

      return null;
    }

    let params: HttpParams  = new HttpParams().set('prefix', path);

    if (delimiter) {

      params = params.set('delimiter', delimiter);
    }

    if (offset) {

      params = params.set('offset', offset.toString());
    }

    if (limit) {

      params = params.set('limit', limit.toString());
    }

    if (key) {

      params = params.set('key', key).set('bkey', this.binaryKey(key));
    }

    if (guid) {

      params = params.set('guid', guid);
    }

    if (type) {

      params = params.set('type', BlobFile.stringOfType(type));
    }

    params = params.set('include_hidden', hidden);

    /* pass back meta for a blob */
    return this.http.get(`/backend/blob/list`, {
      headers,
      params
    });
  }

  listWithTotal(
    hidden:     boolean,
    path:       string,
    delimiter?: string,
    offset?:    number,
    limit?:     number,
    key?:       string,
    guid?:      string,
    type?:      BlobType
  ): Observable<Object> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      Logger.error(`[BlobService] list - no user logged in!`);

      return null;
    }

    let params: HttpParams  = new HttpParams().set('prefix', path);

    if (delimiter) {

      params = params.set('delimiter', delimiter);
    }

    if (offset) {

      params = params.set('offset', offset.toString());
    }

    if (limit) {

      params = params.set('limit', limit.toString());
    }

    if (key) {

      params = params.set('key', key).set('bkey', this.binaryKey(key));
    }

    if (guid) {

      params = params.set('guid', guid);
    }

    if (type) {

      params = params.set('type', BlobFile.stringOfType(type));
    }

    params = params.set('total', true);

    params = params.set('include_hidden', hidden);

    /* pass back meta for a blob */
    return this.http.get(`/backend/blob/list`, {
      headers,
      params
    });
  }
}
